<template>
  <div class="dashboard-grid">
    <!-- Sidebar -->
<aside class="sidebar ai-sidebar">
  <!-- Snackbar/Notification -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="2800" top>
    {{ snackbar.text }}
  </v-snackbar>

  <!-- Header + Dashboard -->
  <div class="sidebar-header">
    <h1 class="logo">🎬 Production Studio</h1>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn icon color="primary" @click="showTour = true" v-on="on">
          <v-icon>mdi-help-circle</v-icon>
        </v-btn>
      </template>
      Start Tour
    </v-tooltip>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn icon color="info" @click="showProjectDashboard = true" v-on="on">
          <v-icon>mdi-view-dashboard</v-icon>
        </v-btn>
      </template>
      Project Dashboard
    </v-tooltip>
  </div>

  <!-- Project Name -->
  <div class="sidebar-project-info">
    <v-text-field
      v-model="project.name"
      label="Project Name"
      dense
      outlined
      dark
      class="project-title"
      :counter="60"
      prepend-inner-icon="mdi-pencil"
    ></v-text-field>
    <div class="current-section">
      <v-icon left small>mdi-folder-open</v-icon>
      <span>{{ sectionTitles[showSection] || 'Section' }}</span>
    </div>
    <v-divider class="mb-3 mt-2"></v-divider>
  </div>

  <!-- Section Navigation -->
  <nav class="sidebar-nav">
    <v-btn v-for="section in sections"
      :key="section.value"
      :color="showSection === section.value ? 'primary' : 'grey darken-2'"
      class="sidebar-btn"
      @click="showSection = section.value"
      :depressed="showSection === section.value"
      block
      large
    >
      <v-icon left>{{ section.icon }}</v-icon>
      {{ section.label }}
    </v-btn>
  </nav>

  <!-- Main Project Actions (Minimal, always visible) -->
  <div class="sidebar-actions" style="margin-bottom: 20px;">
    <v-btn block large color="success" class="mb-2" @click="saveProject">
      <v-icon left>mdi-content-save</v-icon> Save
    </v-btn>
    <v-btn block large color="info" class="mb-2" @click="exportProject">
      <v-icon left>mdi-export</v-icon> Export
    </v-btn>
    <v-btn block large color="error" class="mb-2" @click="resetProject">
      <v-icon left>mdi-restore</v-icon> Reset
    </v-btn>
  </div>

  <!-- AI Suggestions, still useful! -->
  <div class="ai-suggestions mt-2">
    <v-divider class="mb-3"></v-divider>
    <h4 class="suggestion-title">
      <v-icon left color="info">mdi-robot</v-icon> AI Assistant
    </h4>
    <div v-if="aiTip" class="ai-tip">{{ aiTip }}</div>
    <v-btn text color="info" @click="getAITip" small>
      <v-icon left>mdi-refresh</v-icon> New Tip
    </v-btn>
    <v-btn text color="success" @click="autoFillProject" small>
      <v-icon left>mdi-wand</v-icon> Auto-Fill Project
    </v-btn>
    <v-btn text color="info" @click="suggestImprovements" small>
      <v-icon left>mdi-brain</v-icon> Script Feedback
    </v-btn>
  </div>

  <div class="spacer"></div>
  <footer class="sidebar-footer">
    <span>v2.0 AI Studio &copy; 2025</span>
  </footer>
</aside>


<v-dialog v-model="showTour"></v-dialog>

    <main class="main-content">
      <!-- SCENE PANEL -->
<section v-if="showSection==='scene'" class="scene-section">
  <v-card color="primary2" class="pa-5 mb-4 glass-card">
    <div class="flex-between">
      <h2 class="section-title">Scenes in <span style="color:#aef">{{ project.name || 'Untitled Project' }}</span></h2>
      <br />
      <div class="action-toolbar">
        <v-btn color="primary" @click="addNewScene" large>
          <v-icon left>mdi-plus-box</v-icon> New Scene
        </v-btn>
        <v-btn color="info" @click="suggestScene" large>
          <v-icon left>mdi-lightbulb-on-outline</v-icon> Suggest
        </v-btn>
        <v-btn color="warning" @click="undo" large>
          <v-icon left>mdi-undo</v-icon> Undo
        </v-btn>
        <v-btn color="warning" @click="redo" large>
          <v-icon left>mdi-redo</v-icon> Redo
        </v-btn>
      </div>

          </div>
                      <br />
          <v-select
            v-model="currentSceneIndex"
            :items="sceneDropdown"
            label="Switch Scene"
            dense
            outlined
            class="mb-4"
            dark
          ></v-select>
          <v-text-field
            v-model="currentScene.name"
            label="Scene Name"
            dense
            outlined
            dark
            class="scene-title-field"
            :counter="50"
          ></v-text-field>
          <v-form @submit.prevent="generateScene">
            <v-text-field
              v-model="currentScene.prompt"
              label="Describe this scene"
              clearable
              required
              multi-line
              dark
              class="mb-3 inputarea"
              placeholder="E.g., 'A detective interrogates a mysterious suspect in a neon-lit 1980s city at midnight.'"
            ></v-text-field>
            <v-btn icon @click="startVoiceInput('scene')"><v-icon>mdi-microphone</v-icon></v-btn>
            <!-- <v-btn icon @click="startVoiceInput('scenePrompt')"><v-icon>mdi-microphone</v-icon></v-btn> -->
            <v-btn color="primary" type="submit" :loading="loadingScene" class="mb-4">
              Generate Full Scene
            </v-btn>
            <v-btn small color="info" @click="suggestImprovements">Suggest Improvements</v-btn>

            <v-btn color="grey" v-if="currentScene.script" @click="generateScene">
              Regenerate
            </v-btn>
            <v-btn color="error" v-if="project.scenes.length>1" @click="deleteCurrentScene">
              Delete Scene
            </v-btn>
          </v-form>
          <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
          <div v-if="currentScene.script" class="script-block">
            <h3>📝 Scene Script</h3>
            <pre class="scene-script">{{ currentScene.script }}</pre>
          </div>
        </v-card>
      </section>

<section v-if="showSection==='shots'" class="shots-section">
  <v-card class="pa-5 mb-4 glass-card">
    <div class="flex-between">
      <h2 class="section-title">Shots in Scene: <span style="color:#b6f">{{ currentScene.name || 'Untitled Scene' }}</span></h2>
      <br />
      <v-btn small color="success" @click="addShot">+ Add Shot</v-btn>
    </div>
                <br />
    <v-row dense>
      <v-col v-for="(shot, i) in currentScene.sceneBreakdown" :key="i" cols="12" md="6" xl="4">
        <v-card class="pa-3 shot-card glass-card2">
          <div class="flex-between">
            <h4 class="mb-2">Shot {{ i+1 }}</h4>
            <v-btn icon small color="error" @click="deleteShot(i)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
          <v-textarea
            v-model="shot.description"
            label="Shot Description"
            outlined
            dense
            dark
            auto-grow
            class="mb-2"
          ></v-textarea>
          
          <!-- ADD CHARACTER SELECT FOR SHOT HERE -->
          <v-select
            v-model="shot.characterIndexes"
            :items="currentScene.characters.map((c, idx) => ({ text: c.name, value: idx }))"
            label="Characters in Shot"
            multiple
            dark
            dense
            outlined
            class="mb-2"
          ></v-select>
          <!-- END ADDED SELECT -->

          <v-select
            v-model="storyboardStyles[i]"
            :items="styleOptions"
            label="Image Style"
            dark
            dense
            outlined
            class="mb-2"
          ></v-select>
          <v-select
            v-model="storyboardImageSizes[i]"
            :items="imageSizeOptions"
            label="Image Size"
            dark
            dense
            outlined
            class="mb-2"
          ></v-select>
          <v-btn
            small
            color="primary"
            :loading="loadingImages[i]"
            @click="generateStoryboardImage(i)"
            class="mr-2"
          >
            Generate Storyboard
          </v-btn>
          <v-btn small color="grey" @click="editStoryboardPrompt(i)">Edit Prompt</v-btn>
          <div class="mt-2 mb-1">
            <span class="storyboard-label">Prompt:</span>
            <span>{{ shot.storyboardPrompt }}</span>
          </div>
          <div>
            <span class="storyboard-label">Visual Keywords:</span>
            <span>{{ shot.visualKeywords?.join(', ') }}</span>
          </div>
          <div v-if="storyboardImages[i]" class="image-holder">
            <img :src="storyboardImages[i]" class="storyboard-image" />
            <v-btn x-small color="success" @click="downloadImage(storyboardImages[i])">Download</v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</section>

 
      <!-- CHARACTERS PANEL -->
      <section v-if="showSection==='characters'" class="characters-section">
        <v-card class="pa-5 mb-4 glass-card">
          <div class="flex-between">
            <h2 class="section-title">Character Profiles</h2>
            <br />
            <v-btn small color="success" @click="addCharacter">+ Add Character</v-btn>
          </div>
                      <br />
          <v-row dense>
            <v-col v-for="(char, i) in currentScene.characters" :key="i" cols="12" md="6" xl="4">
              <v-card class="pa-3 char-card glass-card2">
                <div class="flex-between">
                  <h4 class="mb-2">{{ char.name }}</h4>
                  <v-btn icon small color="error" @click="deleteCharacter(i)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <v-text-field
                  v-model="char.name"
                  label="Name"
                  dense
                  outlined
                  dark
                  class="mb-2"
                />
                <v-text-field
  v-model="char.role"
  label="Role/Archetype"
  outlined
  dense
  dark
  class="mb-2"
  placeholder="e.g. Detective, Villain, Sidekick"
/>

                <v-textarea
                  v-model="char.description"
                  label="Character Description"
                  outlined
                  dense
                  auto-grow
                  dark
                ></v-textarea>
                <v-select
                  v-model="characterStyles[i]"
                  :items="styleOptions"
                  label="Image Style"
                  dense
                  outlined
                  class="mt-2 mb-2"
                  dark
                ></v-select>
                <v-select
                  v-model="characterImageSizes[i]"
                  :items="imageSizeOptions"
                  label="Image Size"
                  dense
                  outlined
                  class="mb-2"
                  dark
                ></v-select>
<v-text-field
  v-model="char.visualPrompt"
  label="Visual Prompt"
  outlined
  dense
  class="mb-2"
  dark
  auto-grow
  placeholder="E.g. 'Tall, grizzled detective with a scar, 1980s noir trench coat, intense green eyes'"
></v-text-field>

<v-btn
  small
  color="info"
  :loading="loadingCharacterPrompts[i]"
  @click="suggestCharacterVisualPrompt(i)"
  class="mr-2"
>
  Suggest Visual Prompt
</v-btn>
<!-- Show full AI prompt for transparency/debugging -->
<div v-if="char.fullPrompt" class="ai-debug-prompt mb-2" style="font-size: 0.9em; color:#76efff;">
  <strong>AI Prompt:</strong> {{ char.fullPrompt }}
</div>

<v-btn
  small
  color="primary"
  :loading="loadingCharacterImages[i]"
  @click="generateCharacterImage(i)"
  class="mr-2"
>
  Generate Character Image
</v-btn>

                <v-btn small color="success" @click="openCharacterProfile(i)">
   <h4 class="mb-2">View Profile</h4>
                  <v-icon>mdi-account</v-icon>
                </v-btn>
                <div v-if="characterImages[i] && characterImages[i].length" class="image-holder">
                  <div class="char-image-row">
                    <img
                      v-for="(img, j) in characterImages[i]"
                      :key="j"
                      :src="img"
                      class="char-image"
                      @click="setMainCharacterImage(i, j)"
                      :class="{ 'main-img': mainCharacterImage[i] === j }"
                    />
                  </div>
                  <span class="caption">Click an image to set as main profile image.</span>
                  <v-btn x-small color="success" @click="downloadImage(characterImages[i][mainCharacterImage[i]])">Download Main</v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </section>

      <!-- GALLERY PANEL -->
      <section v-if="showSection==='gallery'" class="gallery-section">
        <scene-gallery
          :saved-scenes="project.scenes"
          @load-scene="loadSceneFromGallery"
          @edit-scene="editSceneFromGallery"
          @delete-scene="deleteSceneFromGallery"
        />
      </section>

      <!-- CHARACTER PROFILE MODAL as before -->
      <v-dialog v-model="profileDialog.open" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="headline">Character Profile: {{ currentProfileName }}</span>
          </v-card-title>
          <v-card-text>
            <div>
              <h4>Main Image</h4>
              <img v-if="profileDialog.images.length" :src="profileDialog.images[profileDialog.mainIndex]" class="char-image large" />
            </div>
            <v-divider class="my-3"></v-divider>
            <div>
              <h4>All Images</h4>
              <div class="char-image-row">
                <img
                  v-for="(img, j) in profileDialog.images"
                  :key="j"
                  :src="img"
                  class="char-image"
                  @click="profileDialog.mainIndex = j"
                  :class="{ 'main-img': profileDialog.mainIndex === j }"
                />
              </div>
            </div>
            <v-divider class="my-3"></v-divider>
            <v-textarea v-model="profileDialog.notes" label="Character Notes" auto-grow outlined />
          </v-card-text>
          <v-card-actions>
            <v-btn color="success" @click="saveCharacterProfile">Save Profile</v-btn>
            <v-btn color="error" @click="profileDialog.open = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </main>
  </div>
</template>

<script>
import SceneGallery from '../components/SceneGallery.vue';
const OPENAI_KEY = "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";
export default {
  name: "SceneGeneratorDashboard",
  components: { SceneGallery },
  data() {
    return {
      showSection: "scene",
      sections: [
      { value: 'scene', label: 'Scenes', icon: 'mdi-script' },
      { value: 'shots', label: 'Shots', icon: 'mdi-camera-outline' },
      { value: 'characters', label: 'Characters', icon: 'mdi-account-group' },
      { value: 'gallery', label: 'Gallery', icon: 'mdi-image-multiple' },
    ],
    sectionTitles: {
      scene: 'Scenes',
      shots: 'Shots',
      characters: 'Characters',
      gallery: 'Gallery'
    },
    aiTip: '', // For AI contextual suggestions
      project: {
        name: "Untitled Project", // [NEW]
        scenes: [
          {
            name: "Untitled Scene 1", // [NEW]
            prompt: "",
            script: "",
            sceneBreakdown: [],
            characters: [],
          }
        ]
      },
      currentSceneIndex: 0,
      loadingCharacterPrompts: {},
      showProjectDashboard: false,
      storyboardImages: [],
      storyboardStyles: [],
      storyboardImageSizes: [],
      styleOptions: [
        "Cinematic, hyper-realistic, 35mm film, moody lighting",
        "Cartoon, vibrant, flat colors",
        "Anime style, detailed, dynamic",
        "3D Pixar style, friendly",
        "Photorealistic, soft focus",
        "Noir, high contrast, black and white",
      ],
      imageSizeOptions: [
        { text: "Square (1024x1024)", value: "1024x1024" },
        { text: "HD (1792x1024)", value: "1792x1024" },
        { text: "Portrait (1024x1792)", value: "1024x1792" },
        { text: "Landscape (1344x768)", value: "1344x768" },
      ],
      snackbar: {
  show: false,
  text: '',
  color: 'success'
},
      showTour: false,
      loadingScene: false,
      loadingImages: {},
      error: "",
      characterImages: [],
      characterStyles: [],
      characterImageSizes: [],
      loadingCharacterImages: {},
      mainCharacterImage: {},
      profileDialog: {
        open: false,
        charIndex: null,
        images: [],
        mainIndex: 0,
        notes: "",
      },
      characterProfiles: {},
      undoStack: [],
      redoStack: [],
    };
  },
  computed: {
    sceneDropdown() {
      return this.project.scenes.map((s, i) => ({ text: s.name || `Scene ${i+1}`, value: i }));
    },
    currentScene: {
      get() {
        return this.project.scenes[this.currentSceneIndex];
      },
      set(val) {
        this.$set(this.project.scenes, this.currentSceneIndex, val);
      }
    },
    currentProfileName() {
      if (
        this.profileDialog.charIndex !== null &&
        this.currentScene.characters[this.profileDialog.charIndex]
      ) {
        return this.currentScene.characters[this.profileDialog.charIndex].name;
      }
      return "";
    }
  },
  methods: {
      notify(text, color = 'success') {
    this.snackbar.text = text;
    this.snackbar.color = color;
    this.snackbar.show = true;
  },
     pushToHistory() {
    // Save deep clone before changes
    this.undoStack.push(JSON.stringify(this.project));
    if (this.undoStack.length > 50) this.undoStack.shift();
    this.redoStack = []; // Clear redo after new change
  },
  undo() {
    if (this.undoStack.length === 0) return;
    this.redoStack.push(JSON.stringify(this.project));
    this.project = JSON.parse(this.undoStack.pop());
    this.currentSceneIndex = 0;
  },
  redo() {
    if (this.redoStack.length === 0) return;
    this.undoStack.push(JSON.stringify(this.project));
    this.project = JSON.parse(this.redoStack.pop());
    this.currentSceneIndex = 0;
  },
startVoiceInput(type) {
  // Use standard or prefixed SpeechRecognition for cross-browser support
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    this.notify('Speech recognition not supported in this browser', 'error');
    return;
  }
  const recognition = new SpeechRecognition();
  recognition.lang = 'en-US';
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;

  recognition.onresult = event => {
    const transcript = event.results[0][0].transcript;
    if (type === 'scene') {
      this.currentScene.prompt = transcript;
    } else if (type === 'character') {
      // Extend: if you have a selected character, set the prompt, e.g.
      // this.currentScene.characters[this.selectedCharacterIndex].description = transcript;
    } else if (type === 'shot') {
      // Extend: if you have a selected shot, set the prompt, e.g.
      // this.currentScene.sceneBreakdown[this.selectedShotIndex].description = transcript;
    }
    this.notify('Voice input complete', 'success');
  };
  recognition.onerror = () => {
    this.notify('Voice input error', 'error');
  };
  recognition.start();
},

    // --- Scene CRUD with Name ---
    addNewScene() {
      const newName = `Untitled Scene ${this.project.scenes.length + 1}`;
      this.project.scenes.push({
        name: newName,
        prompt: "",
        script: "",
        sceneBreakdown: [],
        characters: [],
      });
      this.currentSceneIndex = this.project.scenes.length - 1;
    },
    async suggestScene() {
  // Use OpenAI to suggest a random/creative scene prompt
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      "Authorization": `Bearer ${OPENAI_KEY}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      model: "gpt-4o",
      messages: [
        { role: "system", content: "Suggest a creative scene prompt for a film project. Give a short one-sentence description only." }
      ]
    })
  });
  const data = await response.json();
  const suggestion = data.choices?.[0]?.message?.content?.trim();
  this.currentScene.prompt = suggestion;
},
    deleteCurrentScene() {
      if (this.project.scenes.length === 1) return;
      if (confirm("Delete this scene?")) {
        this.project.scenes.splice(this.currentSceneIndex, 1);
        this.currentSceneIndex = 0;
      }
    },
    // --- Shots CRUD ---
addShot() {
  this.currentScene.sceneBreakdown.push({
    description: "New shot description.",
    storyboardPrompt: "Describe the shot visually.",
    visualKeywords: [],
    characterIndexes: [], // <-- add this!
  });
  this.storyboardStyles.push(this.styleOptions[0]);
  this.storyboardImageSizes.push("1024x1024");
},

    deleteShot(i) {
      this.currentScene.sceneBreakdown.splice(i, 1);
      this.storyboardStyles.splice(i, 1);
      this.storyboardImageSizes.splice(i, 1);
      this.storyboardImages.splice(i, 1);
    },
    // --- Character CRUD ---
    addCharacter() {
      this.currentScene.characters.push({
        name: `Character ${this.currentScene.characters.length + 1}`,
        description: "Describe the character...",
      });
      this.characterStyles.push(this.styleOptions[0]);
      this.characterImageSizes.push("1024x1024");
      this.characterImages.push([]);
    },
    deleteCharacter(i) {
      this.currentScene.characters.splice(i, 1);
      this.characterStyles.splice(i, 1);
      this.characterImageSizes.splice(i, 1);
      this.characterImages.splice(i, 1);
    },
    // --- Save/Load/Edit/Export ---
    // saveProject() {
    //   const key = 'scenegen_project';
    //   localStorage.setItem(key, JSON.stringify(this.project));
    //   alert('Project saved to local storage.');
    // },
saveProject() {
  // Ensure the project has an ID
  let id = this.project.id || Date.now().toString();
  this.project.id = id;

  // 1. Update the project index for dashboard/multi-project sup port
  let projectsIndex = JSON.parse(localStorage.getItem('scenegen_projects_index') || '[]');
  let metaIndex = projectsIndex.findIndex(p => p.id === id);
  if (metaIndex === -1) {
    projectsIndex.push({ id, name: this.project.name, lastSaved: Date.now() });
  } else {
    projectsIndex[metaIndex].name = this.project.name;
    projectsIndex[metaIndex].lastSaved = Date.now();
  }
  localStorage.setItem('scenegen_projects_index', JSON.stringify(projectsIndex));

  // 2. Save the current project
  localStorage.setItem(`scenegen_project_${id}`, JSON.stringify(this.project));

  // 3. Save a versioned snapshot (for project versioning/restore)
  let versionKey = `scenegen_project_version_${id}_${Date.now()}`;
  localStorage.setItem(versionKey, JSON.stringify(this.project));

  // 4. Notify user (prefer a snackbar if you have one)
  if (this.notify) {
    this.notify('Project saved!', 'success');
  } else {
    alert('Project saved!');
  }
},


    loadSceneFromGallery(index) {
      this.currentSceneIndex = index;
      this.showSection = 'scene';
    },
    editSceneFromGallery(index) {
      this.currentSceneIndex = index;
      this.showSection = 'scene';
    },
    deleteSceneFromGallery(index) {
      if (this.project.scenes.length === 1) return;
      if (confirm("Delete this scene?")) {
        this.project.scenes.splice(index, 1);
        this.currentSceneIndex = 0;
      }
    },
    resetProject() {
  if (confirm('Are you sure you want to reset this project? All data will be lost.')) {
    this.project = {
      name: "Untitled Project",
      scenes: [
        {
          name: "Untitled Scene 1",
          prompt: "",
          script: "",
          sceneBreakdown: [],
          characters: [],
        }
      ]
    };
    this.currentSceneIndex = 0;
    this.storyboardImages = [];
    this.storyboardStyles = [];
    this.storyboardImageSizes = [];
    this.characterImages = [];
    this.characterStyles = [];
    this.characterImageSizes = [];
    this.loadingImages = {};
    this.loadingCharacterImages = {};
    this.mainCharacterImage = {};
    this.profileDialog = {
      open: false,
      charIndex: null,
      images: [],
      mainIndex: 0,
      notes: "",
    };
    this.characterProfiles = {};
    this.error = "";
    this.showSection = "scene";
    // Optionally clear from localStorage:
    // localStorage.removeItem('scenegen_project');
    // localStorage.removeItem('scenegen_character_profiles');
  }
},

    exportProject() {
      const json = JSON.stringify(this.project, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.project.name.replace(/\s+/g, '_') || 'scenegen-project'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // --- Generate scene via AI ---
    async generateScene() {
      this.error = "";
      this.loadingScene = true;
      this.currentScene.script = "";
      this.currentScene.sceneBreakdown = [];
      this.currentScene.characters = [];
      this.storyboardImages = [];
      this.storyboardStyles = [];
      this.characterImages = [];
      this.characterStyles = [];
      this.loadingImages = {};
      this.loadingCharacterImages = {};
      this.mainCharacterImage = {};
      const systemPrompt = `
You are a professional movie scene generator AI.
Given a brief scene description, perform the following:
1. Write a short, engaging script for the scene (1-3 minutes, with dialogue and actions).
2. Break the scene into a sequence of visual shots. For each shot:
    - Provide a 1-2 sentence description.
    - Create a DALL-E style image prompt for the storyboard (describe the shot visually, including mood, lighting, characters, setting, and camera angle).
    - Extract 2-3 visual keywords for image/video search (should be short and visually concrete).
3. Identify all key characters in the scene and write a brief (1-2 sentences) description for each (name, look, personality).
4. Return all output as a single JSON object in this format:
{
  "script": "...",
  "sceneBreakdown": [
    {
      "description": "...",
      "storyboardPrompt": "...",
      "visualKeywords": ["...", "..."]
    }
  ],
  "characters": [
    {
      "name": "...",
      "description": "..."
    }
  ]
}
Strictly return valid JSON only, no markdown or prose.
      `.trim();

      try {
        const response = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${OPENAI_KEY}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            model: "gpt-4o",
            messages: [
              { role: "system", content: systemPrompt },
              { role: "user", content: this.currentScene.prompt }
            ],
            temperature: 0.8,
            max_tokens: 4000
          })
        });

        const data = await response.json();
        let content = data.choices?.[0]?.message?.content || "";
        content = content.replace(/^\s*```json|```$/g, '').trim();
        const parsed = JSON.parse(content);
        this.currentScene.script = parsed.script;
        this.currentScene.sceneBreakdown = parsed.sceneBreakdown || [];
        this.currentScene.characters = parsed.characters || [];
        this.storyboardImages = Array(this.currentScene.sceneBreakdown.length).fill(null);
        this.storyboardStyles = Array(this.currentScene.sceneBreakdown.length).fill(this.styleOptions[0]);
        this.storyboardImageSizes = Array(this.currentScene.sceneBreakdown.length).fill(this.imageSizeOptions[0].value);
        this.characterImages = Array(this.currentScene.characters.length).fill().map(() => []);
        this.characterStyles = Array(this.currentScene.characters.length).fill(this.styleOptions[0]);
        this.characterImageSizes = Array(this.currentScene.characters.length).fill(this.imageSizeOptions[0].value);
        this.mainCharacterImage = {};
      } catch (err) {
        this.error = "AI failed to generate the scene. " + (err.message || "");
      } finally {
        this.loadingScene = false;
      }
    },
    async getAITip() {
  // Fast fallback while waiting for AI
  this.aiTip = "Generating a smart tip...";
  
  // You can send current project/section for context
  const context = `The user is working on the "${this.project.name}" project. Current section: ${this.sectionTitles[this.showSection]}.`;
  const systemPrompt = `
You are an AI assistant for a film scene generator app. Give a short, actionable tip or suggestion (1-2 sentences max) that will help a user be more productive, creative, or discover new features in the app. Be contextual and inspiring. 
Format: no preface, just the tip.
Example: "Use voice input to quickly describe a new scene prompt."
`;

  try {
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENAI_KEY}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model: "gpt-4o",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: context }
        ],
        temperature: 0.85,
        max_tokens: 60
      })
    });
    const data = await response.json();
    const aiTipRaw = data.choices?.[0]?.message?.content?.trim();
    // Fallback if OpenAI fails or returns nothing
    this.aiTip = aiTipRaw || "Try using voice input for a faster workflow.";
  } catch (err) {
    // Fallback to local tip on error
    const tips = [
      "Try using voice input for a faster workflow.",
      "Use AI Auto-Fill to kickstart your next project.",
      "Add characters to your scene for richer scripts.",
      "Drag scenes to reorder and improve flow.",
      "Click 'Suggest' for a creative scene prompt.",
      "Use 'Undo' if you make a mistake."
    ];
    this.aiTip = tips[Math.floor(Math.random() * tips.length)];
  }
},

    // --- Image Generation, Edit, Download, etc... (same as above) ---
async generateStoryboardImage(shotIndex) {
  const shot = this.currentScene.sceneBreakdown[shotIndex];
  if (!shot || !shot.storyboardPrompt) return;
  this.$set(this.loadingImages, shotIndex, true);
  const style = this.storyboardStyles[shotIndex] || this.styleOptions[0];
  const size = this.storyboardImageSizes[shotIndex] || "1024x1024";

  let charactersSummary = '';
  if (shot.characterIndexes && shot.characterIndexes.length && this.currentScene.characters) {
    charactersSummary =
      'Characters in this shot: ' +
      shot.characterIndexes
        .map(idx => {
          const c = this.currentScene.characters[idx];
          return c ? `${c.name} (${c.description})` : '';
        })
        .filter(Boolean)
        .join('; ') +
      '.';
  } else if (this.currentScene.characters && this.currentScene.characters.length) {
    charactersSummary =
      'Characters in this shot: ' +
      this.currentScene.characters
        .map(c => `${c.name} (${c.description})`)
        .join('; ') +
      '.';
  }

  const prompt = `
${shot.storyboardPrompt}
${charactersSummary}
Scene context: ${this.currentScene.name || ''}
Script summary: ${this.currentScene.script ? this.currentScene.script.slice(0, 180) : ''}
Style: ${style}.
  `.trim();

  try {
    const resp = await fetch("https://api.openai.com/v1/images/generations", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENAI_KEY}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model: "dall-e-3",   // <-- Upgrade here!
        prompt,
        n: 1,
        size,
      })
    });
    const data = await resp.json();
    const imgUrl = data.data?.[0]?.url;
    if (imgUrl) {
      this.$set(this.storyboardImages, shotIndex, imgUrl);
    } else {
      this.$set(this.storyboardImages, shotIndex, null);
    }
  } catch (err) {
    this.$set(this.storyboardImages, shotIndex, null);
    alert("Failed to generate image for this shot.");
  } finally {
    this.$set(this.loadingImages, shotIndex, false);
  }
},

async generateCharacterImage(charIndex) {
  const char = this.currentScene.characters[charIndex];
  if (!char || !char.description) return;
  this.$set(this.loadingCharacterImages, charIndex, true);

  const style = this.characterStyles[charIndex] || this.styleOptions[0];
  const size = this.characterImageSizes[charIndex] || "1024x1024";

  const visualPrompt = char.visualPrompt?.trim()
    ? char.visualPrompt.trim()
    : `${char.name}: ${char.description}`;

  const fullPrompt = `Portrait of ${char.name}, ${visualPrompt}. ${char.role ? "Role: " + char.role + '.' : ""} Style: ${style}.`;

  this.$set(this.currentScene.characters[charIndex], "fullPrompt", fullPrompt);

  try {
    const resp = await fetch("https://api.openai.com/v1/images/generations", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENAI_KEY}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model: "dall-e-3",   // <-- Upgrade here!
        prompt: fullPrompt,
        n: 1,
        size,
      })
    });
    const data = await resp.json();
    const imgUrl = data.data?.[0]?.url;
    if (imgUrl) {
      if (!this.characterImages[charIndex]) this.$set(this.characterImages, charIndex, []);
      this.characterImages[charIndex].push(imgUrl);
      if (!this.mainCharacterImage[charIndex]) this.$set(this.mainCharacterImage, charIndex, 0);
    }
  } catch (err) {
    alert("Failed to generate image for this character.");
  } finally {
    this.$set(this.loadingCharacterImages, charIndex, false);
  }
},

async suggestCharacterVisualPrompt(charIndex) {
  this.$set(this.loadingCharacterPrompts, charIndex, true);
  const char = this.currentScene.characters[charIndex];
  if (!char) return;
  const systemPrompt = `
You are a creative film AI assistant. Given a character's name and description, generate a highly visual, cinematic, and specific prompt for AI image generation. Include details about facial features, clothing, mood, setting, ethnicity if possible, and unique attributes. 
Example: "Tall, grizzled detective with a long trench coat, sharp green eyes, subtle scar on cheek, 1980s neon-lit city background, intense expression, noir style."
Return only the prompt.
`.trim();

  try {
    const resp = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENAI_KEY}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        model: "gpt-4o",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: `Name: ${char.name}\nDescription: ${char.description}` }
        ],
        temperature: 0.85,
        max_tokens: 80
      })
    });
    const data = await resp.json();
    const aiPrompt = data.choices?.[0]?.message?.content?.trim();
    if (aiPrompt) {
      this.$set(this.currentScene.characters[charIndex], "visualPrompt", aiPrompt);
    }
  } catch (err) {
    alert("Failed to suggest visual prompt.");
  } finally {
    this.$set(this.loadingCharacterPrompts, charIndex, false);
  }
},

    editStoryboardPrompt(i) {
      const newPrompt = prompt("Edit storyboard prompt:", this.currentScene.sceneBreakdown[i].storyboardPrompt);
      if (newPrompt !== null) this.currentScene.sceneBreakdown[i].storyboardPrompt = newPrompt;
    },
    setMainCharacterImage(charIndex, imgIndex) {
      this.$set(this.mainCharacterImage, charIndex, imgIndex);
    },
    openCharacterProfile(charIndex) {
      this.profileDialog.charIndex = charIndex;
      this.profileDialog.images = this.characterImages[charIndex] || [];
      this.profileDialog.mainIndex = this.mainCharacterImage[charIndex] || 0;
      this.profileDialog.notes = this.characterProfiles[charIndex]?.notes || "";
      this.profileDialog.open = true;
    },
    saveCharacterProfile() {
      const idx = this.profileDialog.charIndex;
      this.mainCharacterImage[idx] = this.profileDialog.mainIndex;
      this.characterProfiles[idx] = {
        images: [...this.profileDialog.images],
        mainIndex: this.profileDialog.mainIndex,
        notes: this.profileDialog.notes,
      };
      this.profileDialog.open = false;
      localStorage.setItem('scenegen_character_profiles', JSON.stringify(this.characterProfiles));
    },
    downloadImage(url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'scene-image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
    watch: {
    project: {
      handler(newProject) {
        localStorage.setItem('scenegen_project', JSON.stringify(newProject));
      },
      deep: true
    },
    characterProfiles: {
      handler(newProfiles) {
        localStorage.setItem('scenegen_character_profiles', JSON.stringify(newProfiles));
      },
      deep: true
    }
  },

  mounted() {
      this.getAITip();
    const saved = localStorage.getItem('scenegen_project');
    if (saved) {
      this.project = JSON.parse(saved);
    }
    const savedProfiles = localStorage.getItem('scenegen_character_profiles');
    if (savedProfiles) {
      this.characterProfiles = JSON.parse(savedProfiles);
    }
    if (!localStorage.getItem('scenegen_tour_shown')) {
      this.showTour = true;
      localStorage.setItem('scenegen_tour_shown', '1');
    } else {
      this.showTour = false;
    }
  },
  beforeDestroy() {
    localStorage.setItem('scenegen_project', JSON.stringify(this.project));
    localStorage.setItem('scenegen_character_profiles', JSON.stringify(this.characterProfiles));
  }
};
</script>

<style scoped>
.ai-sidebar {
  background: linear-gradient(145deg, #1a243b 80%, #255b85 60%);
  padding: 28px 16px 14px 16px;
  color: #eaf6ff;
  display: flex;
  flex-direction: column;
  border-right: 2.5px solid #2968adbb;
  min-height: 100vh;
  min-width: 250px;
  max-width: 340px;
  box-shadow: 0 0 64px #00eeff11, 0 0 0 #2bf8f944;
  z-index: 3;
  transition: background 0.3s;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.logo {
  font-family: 'Orbitron', monospace;
  font-size: 1.35rem;
  color: #66e5fa;
  text-shadow: 0 0 10px #0097fb88;
  letter-spacing: 1px;
  margin: 0;
}

.sidebar-project-info {
  margin-bottom: 20px;
}

.current-section {
  font-size: 1.07em;
  color: #94f3ff;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}
.sidebar-nav {
  margin-bottom: 13px;
}
.sidebar-btn {
  margin-bottom: 11px;
  font-size: 1.17em !important;
  text-align: left;
  letter-spacing: 0.04em;
  border-radius: 13px !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 10px #2187ee19;
  background: rgba(41, 120, 182, 0.16) !important;
  transition: background 0.2s;
}
.sidebar-btn .v-icon {
  margin-right: 10px;
}
.sidebar-btn.v-btn--depressed, .sidebar-btn[aria-pressed="true"] {
  background: linear-gradient(90deg,#44cffc 0%,#2690f7 100%) !important;
  color: #fff !important;
  box-shadow: 0 4px 20px #2690f766;
}
.sidebar-actions {
  margin-bottom: 6px;
}
.action-toolbar {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 7px;
}
.action-btn {
  font-size: 1.12em !important;
  border-radius: 12px !important;
  padding: 12px 18px !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 8px #31dbfa11;
  text-transform: none !important;
  min-width: 145px;
}
.action-btn .v-icon {
  margin-right: 8px;
}
.ai-suggestions {
  background: rgba(26,36,62,0.76);
  border-radius: 13px;
  padding: 14px 14px 8px 14px;
  box-shadow: 0 1.5px 10px #44c7f944;
  color: #b5f1ff;
  margin-top: 8px;
}
.suggestion-title {
  font-size: 1.12em;
  font-weight: 700;
  margin-bottom: 7px;
  color: #49e0fa;
  letter-spacing: 0.01em;
}
.ai-tip {
  font-size: 1em;
  color: #c7f8ff;
  margin-bottom: 7px;
  min-height: 1.9em;
  transition: color 0.2s;
}
.sidebar-footer {
  font-size: 0.92em;
  color: #81d5f9;
  text-align: center;
  margin-top: 22px;
  opacity: 0.6;
}
@media (max-width: 900px) {
  .ai-sidebar {
    min-width: 0;
    max-width: 100vw;
    padding: 16px 4vw 10px 4vw;
  }
  .sidebar-btn, .action-btn {
    min-width: 0;
    width: 100%;
    font-size: 1em !important;
    padding: 10px 9px !important;
  }
  .action-toolbar {
    flex-direction: column;
    gap: 9px;
  }
}

.glass-card {
  background: rgba(18, 27, 47, 0.86);
  border-radius: 18px;
  box-shadow: 0 8px 28px 0 #161d2a44, 0 1.5px 12px 0 #0097fb33, 0 0.5px 1px #7ad6f733;
  border: 1.2px solid #44bbff33;
  backdrop-filter: blur(7px) saturate(1.3);
}

.glass-card2 {
  background: rgba(28, 39, 68, 0.84);
  border-radius: 15px;
  box-shadow: 0 4px 18px #45a2ff22, 0 1px 4px #92f2ff22;
  border: 1px solid #66d1ff29;
  backdrop-filter: blur(5px) saturate(1.15);
}

.section-title, .scene-title {
  font-family: 'Orbitron', 'Fira Mono', monospace;
  color: #93e0ff;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 10px #0097fb19;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.project-title {
  font-size: 1.21em;
  font-weight: bold;
  height: 60px;
  line-height: 40px;
  color: #b6e3fc !important;
  margin-bottom: 12px;
  background: rgba(80,120,170,0.09) !important;
  border-radius: 8px;
  border: 1.3px solid #43b5fd21 !important;
  box-shadow: 0 2px 16px #67ecff19;
}

.scene-title-field {
  font-size: 1.12em;
  color: #d8f3ff !important;
  font-weight: 600;
}

/* --- FUTURISTIC MOVIE STUDIO STYLE --- */
.dashboard-grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  min-height: 100vh;
  background: linear-gradient(120deg,#181b29 60%,#324069 100%);
}
.sidebar {
  background: rgba(18, 23, 42, 0.492);
  padding: 30px 16px 14px 16px;
  color: #eaf6ff;
  display: flex;
  flex-direction: column;
  /* border-right: 2.5px solid #2968adbb; */
  min-height: 100vh;
  min-width: 220px;
  max-width: 320px; /* Add this */
  width: 260px;      /* Add this */
  /* box-shadow: 0 0 48px #00eeff16, 0 0 0 #2bf8f944; */
  z-index: 3;
}
.action-toolbar {
  display: flex;
  gap: 18px; /* space between buttons */
  flex-wrap: wrap; /* let them break to new row on mobile */
  margin-bottom: 20px;
  justify-content: flex-start; /* align to left; use center for centered buttons */
}

.action-btn {
  font-size: 1.22em;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 14px 26px;
  border-radius: 14px;
  box-shadow: 0 3px 16px #31dbfa19, 0 1px 2px #0b82c222;
  background: linear-gradient(120deg, #151b2b 80%, #2690f7 100%);
  color: #eafcff !important;
  text-transform: none !important;
  transition: box-shadow 0.2s, background 0.2s, transform 0.15s;
  min-width: 170px;
}

.action-btn .v-btn__content {
  font-size: 1.07em;
}

.action-btn:hover, .action-btn:focus {
  background: linear-gradient(120deg, #2690f7 50%, #43e9ff 100%);
  box-shadow: 0 8px 22px #43e9ff22, 0 1.5px 7px #00eaff33;
  color: #fff !important;
  transform: translateY(-2px) scale(1.03);
}

@media (max-width: 700px) {
  .action-toolbar {
    gap: 10px;
    flex-direction: column;
    align-items: stretch;
  }
  .action-btn {
    min-width: 0;
    width: 100%;
    padding: 14px 10px;
    font-size: 1.1em;
  }
}

.logo {
  font-family: 'Orbitron', monospace;
  font-size: 1.65rem;
  margin-bottom: 30px;
  color: #66e5fa;
  text-shadow: 0 0 14px #0097fb77;
  letter-spacing: 1.2px;
}
.sidebar .v-btn {
  margin-bottom: 17px;
  font-weight: 600;
}
.spacer {
  flex-grow: 1;
}
.main-content {
  padding: 42px 3vw 46px 3vw;
  min-width: 0;
}
.section-title {
  font-family: 'Orbitron', monospace;
  color: #67f2ff;
  font-size: 1.5rem;
  margin-bottom: 18px;
  letter-spacing: 1px;
}
.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.script-block {
  background: rgba(24,34,60,0.97);
  border-radius: 13px;
  margin: 0.7em 0 0 0;
  padding: 1em 1em;
  color: #b6eaff;
  font-size: 1.11em;
  font-family: 'Fira Mono', 'Roboto Mono', monospace;
  box-shadow: 0 0 16px #38affb1c;
  border: 1.5px solid #21c4e7;
}
.shot-card, .char-card, .glass-card, .glass-card2 {
  background: rgba(32,41,71,0.87) !important;
  border-radius: 17px !important;
  box-shadow: 0 0 22px #0097fb44, 0 1.5px 0 #19e3ff33;
  margin-bottom: 25px;
  border: 1.5px solid #289cf4bb;
}
.glass-card2 {
  background: rgba(36,52,91,0.79) !important;
  border: 1.5px solid #2ac2f7aa;
}
.inputarea {
  background: rgba(32,41,71,0.94) !important;
  border-radius: 8px !important;
  color: #aef;
  font-size: 1.25rem !important;
}
.storyboard-label {
  font-size: 0.96rem;
  font-weight: 600;
  color: #74e4ff;
  margin-top: 8px;
}
.image-holder {
  margin-top: 8px;
  margin-bottom: 7px;
  text-align: center;
}
.storyboard-image {
  margin-top: 7px;
  margin-bottom: 6px;
  border-radius: 11px;
  box-shadow: 0 0 14px #1bfbfb44;
  max-width: 100%;
  display: block;
}
.char-image-row {
  display: flex;
  flex-wrap: wrap;
  gap: 11px;
  margin: 8px 0 0 0;
  align-items: flex-end;
}
.char-image {
  width: 130px;
  height: 130px;
  object-fit: cover;
  border-radius: 11px;
  box-shadow: 0 0 13px #2cfbfb55;
  border: 2.5px solid #3cc3ff33;
  cursor: pointer;
  margin-right: 7px;
  transition: border 0.2s, transform 0.18s;
}
.char-image.main-img, .char-image.large {
  border: 3.5px solid #65ecff;
  box-shadow: 0 0 22px #31dfff61;
  transform: scale(1.08);
}
.char-image.large {
  width: 170px;
  height: 170px;
  margin: 0 auto 12px auto;
  display: block;
}
.caption {
  display: block;
  font-size: 0.95em;
  color: #bbefff;
  margin-top: 3px;
}
@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .sidebar {
    min-width: 0;
    padding-bottom: 5vw;
  }
  .main-content {
    padding: 20px 2vw 22px 2vw;
  }
}
</style>
